import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useAnimation } from "framer-motion";
import Confetti from "react-confetti";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { useInView } from "react-intersection-observer";
import html2canvas from "html2canvas";
import sucessAnimation from "../../assets/animations/congrats-anim.json";
import Lottie from "lottie-react";

const tiers = ["Bronze", "Silver", "Gold", "Platinum", "Diamond"];
const levels = [3, 2, 1]; // Levels per tier

const tierLevels = [
  "Bronze 3",
  "Bronze 2",
  "Bronze 1",
  "Silver 3",
  "Silver 2",
  "Silver 1",
  "Gold 3",
  "Gold 2",
  "Gold 1",
  "Platinum 3",
  "Platinum 2",
  "Platinum 1",
  "Diamond 3",
  "Diamond 2",
  "Diamond 1",
];

const getTierAndLevelShorter = (versesCompleted: number) => {
  const totalVerses = 6236; // Total number of verses in the Quran
  const totalLevels = tierLevels.length;
  const levelSize = totalVerses / totalLevels; // Verses required per level

  const levelIndex = Math.min(
    Math.floor(versesCompleted / levelSize),
    totalLevels - 1
  );

  const current = tierLevels[levelIndex];
  const next = tierLevels[levelIndex + 1] || "Max Tier Reached";

  // Calculate progress towards the next level
  const levelStart = levelIndex * levelSize;
  const progressInCurrentLevel = versesCompleted - levelStart;
  const currentLevelProgress = Math.min(
    100,
    (progressInCurrentLevel / levelSize) * 100
  );

  // Calculate total completion percentage
  const totalCompletionPercentage = Math.min(
    100,
    (versesCompleted / totalVerses) * 100
  );

  return {
    current,
    next,
    currentLevelProgress,
    totalCompletionPercentage, // ✅ Added total progress percentage
  };
};

const getTierAndLevelShorterOld = (progress: number) => {
  const totalLevels = tierLevels.length;
  const levelSize = 100 / totalLevels; // Progress required per level
  // console.log(levelSize);

  const levelIndex = Math.min(
    Math.floor(progress / levelSize),
    totalLevels - 1
  );

  const current = tierLevels[levelIndex];
  const next = tierLevels[levelIndex + 1] || "Max Tier Reached";

  // Calculate progress towards the next level
  const levelStart = levelIndex * levelSize;
  const progressInCurrentLevel = progress - levelStart;
  const currentLevelProgress = Math.min(
    100,
    (progressInCurrentLevel / levelSize) * 100
  );

  return {
    current,
    next,
    currentLevelProgress,
  };
};

/*
const getTierAndLevel = (progress: number) => {
  // Each level requires this much progress
  const levelSize = 100 / (tiers.length * levels.length);
  const totalLevels = tiers.length * levels.length;

  // Determine the current level index (0-indexed) from overall progress
  const levelIndex = Math.min(
    Math.floor(progress / levelSize),
    totalLevels - 1
  );

  // Determine which tier and level within the tier we are in
  const tierIndex = Math.floor(levelIndex / levels.length);
  const levelInTierIndex = levelIndex % levels.length;

  const currentTier = tiers[tierIndex];
  const currentLevel = levels[levelInTierIndex];

  // Determine the next level within the current tier or in the next tier
  let nextLevel;
  let nextTier = currentTier; // Keep the current tier by default

  if (levelInTierIndex + 1 < levels.length) {
    // Next level exists in the same tier
    nextLevel = levels[levelInTierIndex + 1];
  } else {
    // Move to the first level of the next tier only if it exists
    nextLevel = levels[0];
    if (tierIndex + 1 < tiers.length) {
      nextTier = tiers[tierIndex + 1]; // Move to the next tier
    }
  }

  // If current tier is the last tier and the level is the last level, mark as Max Tier Reached
  if (
    tierIndex === tiers.length - 1 &&
    levelInTierIndex === levels.length - 1
  ) {
    nextTier = "Max Tier Reached";
    nextLevel = "Max";
  }

  // Calculate the progress within the current level:
  const levelStart = levelIndex * levelSize;
  const progressInCurrentLevel = progress - levelStart;
  const currentLevelProgress = Math.min(
    100,
    Math.max(0, (progressInCurrentLevel / levelSize) * 100)
  );

  return {
    currentTier,
    currentLevel,
    nextTier,
    nextLevel,
    lastTier: tierIndex > 0 ? tiers[tierIndex - 1] : "Starting Tier",
    currentLevelProgress,
  };
};
*/

interface AchivementListCardProps {
  versesCompleted: number;
}
const AchivementListCard: React.FC<AchivementListCardProps> = ({
  versesCompleted,
}) => {
  const navigate = useNavigate();

  const vistButtonLink = (link: string) => {
    navigate(link);
  };

  // Use getTierAndLevelShorter instead
  const { current, next, currentLevelProgress, totalCompletionPercentage } =
    getTierAndLevelShorter(versesCompleted);

  const [prevTier, setPrevTier] = useState(() => {
    return localStorage.getItem("userTier") || `${current}`; // Default to lowest tier
  });

  const [showConfetti, setShowConfetti] = useState(false);
  const controls = useAnimation();

  useEffect(() => {
    const storedTier = localStorage.getItem("userTier");

    if (storedTier !== String(current)) {
      // Convert both to string
      setShowConfetti(true);

      const timeout = setTimeout(() => setShowConfetti(false), 6000);

      setPrevTier(current);
      localStorage.setItem("userTier", String(current));

      return () => clearTimeout(timeout); // Cleanup timeout
    }

    controls.start({
      rotate: 360,
      transition: { duration: 2, ease: "easeInOut", loop: Infinity },
    });
  }, [current, controls]); // prevTier removed

  const achievementRef = useRef<HTMLDivElement>(null);
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.5,
  });

  const shareAchievement = async () => {
    if (!achievementRef.current) return;

    const canvas = await html2canvas(achievementRef.current);
    const image = canvas.toDataURL("image/png");

    const link = document.createElement("a");
    link.href = image;
    link.download = "achievement.png";
    link.click();
  };

  const [confettiSettings, setConfettiSettings] = useState({
    width: 0,
    height: 0,
    x: 0,
    y: 0,
  });

  useEffect(() => {
    setConfettiSettings({
      width: window.innerWidth, // Full viewport width
      height: window.innerHeight, // Full viewport height
      x: 0, // Start from the top left of the viewport
      y: 0, // Align to viewport instead of component position
    });
  }, [versesCompleted]);

  return (
    <>
      <div
        ref={achievementRef}
        className={`relative w-full px-6 py-6 ${
          totalCompletionPercentage === 100
            ? "bg-custom-gradient"
            : "bg-custom-gradient-achivements"
        } flex flex-col rounded-[2.8em]`}
      >
        {(showConfetti || totalCompletionPercentage === 100) && (
          <div className="fixed top-0 left-0 w-screen h-screen pointer-events-none z-50">
            <Confetti
              width={confettiSettings.width}
              height={confettiSettings.height}
              numberOfPieces={1800}
              recycle={false}
              gravity={0.07}
            />
          </div>
        )}

        {totalCompletionPercentage === 100 ? (
          <div className="">
            <Lottie animationData={sucessAnimation} loop={true} />
          </div>
        ) : (
          <>
            <h3 className="font-grgrifter text-[1.5em] tracking-wider text-[#1B1B1B] py-3 px-2">
              Latest Achievements
            </h3>
            <motion.div
              ref={ref}
              className="px-14 py-3 mt-6 flex flex-col justify-center items-center gap-4"
            >
              <CircularProgressbar
                className="font-grgrifter"
                value={currentLevelProgress}
                text={
                  totalCompletionPercentage === 100
                    ? "Alhamdulillah!"
                    : `${Math.round(currentLevelProgress)}%`
                }
                styles={buildStyles({
                  textSize: totalCompletionPercentage === 100 ? "10px" : "16px",
                  pathColor:
                    totalCompletionPercentage === 100 ? "#3a9437" : "#c46b19",
                  textColor:
                    totalCompletionPercentage === 100 ? "#3a9437" : "#FFF",
                  trailColor: "#F2F2F2",
                  strokeLinecap: "round",
                })}
              />
              <div className="h-6">
                <p className="text-[#985c23b4] text-sm">
                  Next Tier: <span className="text-[#985c23]">{next}</span>
                </p>
              </div>
            </motion.div>
          </>
        )}
        {totalCompletionPercentage === 100 ? (
          <div className="flex flex-col justify-center items-center gap-6">
            <p className="mr-6 flex flex-col">
              <span className="font-bold text-center text-lg text-[#045B01]">
                What an incredible achievement! Completing the Quran is not just
                an end, but a beautiful beginning. May Allah increase you in
                knowledge and reward you abundantly!
              </span>
            </p>
            <button
              className="w-full flex items-center justify-center px-4 py-4 bg-[#045B01] rounded-[2em] cursor-pointer"
              type="submit"
              onClick={shareAchievement}
            >
              <p className="text-lg tracking-wider text-center text-white">
                Share Your Achievements
              </p>
            </button>
          </div>
        ) : (
          <div className="flex justify-between items-center">
            <p className="mr-6 flex flex-col">
              You've unlocked <span className="font-bold">{current} Badge</span>
            </p>
            <button
              className="w-[10em] flex items-center justify-center px-4 py-5 bg-[#C46B19] rounded-[2em] cursor-pointer"
              type="submit"
              onClick={shareAchievement}
            >
              <p className="text-sm tracking-wider text-center text-white">
                Share Achievements
              </p>
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default AchivementListCard;
